import { memo } from 'react'
import { useTranslation } from '@spcc/core'
import {
  MapPin,
  Badge,
  TableRow,
  TableCell,
  TenantCell,
  cn,
} from '@spcc/uikit'
import { statusConfig, defaultStatusConfig } from '../constants'
import type { ShipmentRowProps } from '../types'

function ShipmentRowComponent({
  shipment,
  isSelected,
  showTenantColumn,
  onClick,
  onDoubleClick,
  isArabic,
}: ShipmentRowProps) {
  const { t } = useTranslation()
  const config = statusConfig[shipment.status] ?? defaultStatusConfig

  return (
    <TableRow
      className={cn(
        "border-border/30 cursor-pointer group transition-colors duration-150",
        isSelected
          ? "bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20"
          : "hover:bg-muted/50"
      )}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <TableCell className="px-5 py-4">
        <span className="font-semibold text-foreground text-sm">
          {shipment.shipmentNumber}
        </span>
      </TableCell>
      {showTenantColumn && (
        <TableCell className="px-5 py-4">
          <TenantCell tenant={shipment.tenant} />
        </TableCell>
      )}
      <TableCell className="px-5 py-4">
        <div className="flex flex-col">
          <span className="text-foreground text-sm">
            {isArabic ? shipment.product.ar : shipment.product.en}
          </span>
          <span className="text-muted-foreground text-xs">
            {shipment.quantity.value} {shipment.quantity.unit}
          </span>
        </div>
      </TableCell>
      <TableCell className="px-5 py-4">
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-muted-foreground text-sm">{shipment.origin}</span>
        </div>
      </TableCell>
      <TableCell className="px-5 py-4">
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-primary" />
          <span className="text-foreground text-sm">{shipment.destination}</span>
        </div>
      </TableCell>
      <TableCell className="px-5 py-4">
        <Badge className={cn(config.bg, config.text, "border-0 font-semibold px-3 py-1 shadow-sm flex items-center gap-1.5")}>
          {(() => {
            const StatusIcon = config.icon
            return <StatusIcon className="w-3.5 h-3.5" />
          })()}
          <span className={cn("w-2 h-2 rounded-full animate-pulse", config.dotColor)} />
          {t(`shipments.${shipment.status}`)}
        </Badge>
      </TableCell>
    </TableRow>
  )
}

export const ShipmentRow = memo(ShipmentRowComponent, (prev, next) => {
  return (
    prev.shipment.id === next.shipment.id &&
    prev.isSelected === next.isSelected &&
    prev.shipment.status === next.shipment.status &&
    prev.showTenantColumn === next.showTenantColumn &&
    prev.isArabic === next.isArabic
  )
})
